import { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { MasterDocument } from '@util/firestore/master';
import { trackMasterEvent } from '@util/analytics/masterTracking';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip);

const Line = dynamic(() => import('react-chartjs-2').then((m) => m.Line), { ssr: false });

import type { PricingApiResponse } from '../../pages/api/market/pricing';
import {
  MIN_6MO_RECENT,
  MIN_12MO_CINEMA,
  MIN_12MO_DEFAULT,
  classifyCinema,
  formatPrice,
  ConditionTab,
} from './pricingGuideGating';

// --- "Latest sale" freshness line ------------------------------------------
// Show the "Latest sale: <date>" line ONLY when the newest approved sale is
// within this many days. Older than that, hide the line entirely (the price
// still renders if the gate passes) — a months-old "latest sale" reads as
// stale, which we would rather not advertise.
const DATE_FRESH_DAYS = 14;

// --- Trend badge -----------------------------------------------------------
// Minimum approved sales required in EACH 90-day window before the
// appreciating/depreciating badge is eligible. With fewer, a single outlier
// can flip the direction — worse than showing nothing.
const TREND_MIN_PER_WINDOW = 5;

// KILL SWITCH: while the sales matcher is paused/draining, the 90-day windows
// contain biased partial data that can flip the trend direction. Suppress the
// badge entirely until the matcher is caught up, THEN SET THIS TO false.
// (The >= TREND_MIN_PER_WINDOW gate stays in place for when it is re-enabled.)
const SUPPRESS_TREND_BADGE = true;

/** Is an ISO "YYYY-MM-DD" date within the last `days` days (inclusive)? */
function isWithinDays(isoDate: string, days: number): boolean {
  const sold = new Date(isoDate);
  if (isNaN(sold.getTime())) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return sold >= cutoff;
}

/** "2026-05-14" → "May 14, 2026" */
function formatLatestSaleDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ─── Local types ───────────────────────────────────────────────────────────
interface MarketValuations {
  bySource?: {
    [key: string]: {
      avg: number;
      median?: number;
      min: number;
      max: number;
      count: number;
    };
  };
}

interface PricingGuideSectionProps {
  master: MasterDocument;
  sellUrl: string;
  onVisibilityChange?: (visible: boolean) => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────
/** "2025-10" → "Oct 25" */
function formatMonthLabel(yyyyMm: string): string {
  const [year, month] = yyyyMm.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  const mon = date.toLocaleString('en-US', { month: 'short' });
  const yr = String(year).slice(2);
  return `${mon} ${yr}`;
}

type TimeWindow = '3mo' | '6mo' | '1yr';

const TIME_WINDOW_LABELS: Record<TimeWindow, string> = {
  '3mo': '3 months',
  '6mo': '6 months',
  '1yr': '1 year',
};

function filterByWindow(history: PricingApiResponse['priceHistory'], window: TimeWindow) {
  if (!history.length) return history;
  const monthsBack = window === '3mo' ? 3 : window === '6mo' ? 6 : 12;
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  return history.filter(({ month }) => {
    const [y, m] = month.split('-');
    return new Date(Number(y), Number(m) - 1, 1) >= cutoff;
  });
}

// ─── Loading skeleton ──────────────────────────────────────────────────────
function Skeleton() {
  return (
    <section id="pricing-guide" aria-label="Pricing Guide">
      <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">
        <div className="animate-pulse space-y-[1.6rem]">
          <div className="bg-gray-200 rounded h-[4.8rem] w-full" />
          <div className="bg-gray-200 rounded h-[8rem] w-full" />
          <div className="bg-gray-200 rounded h-[6rem] w-full" />
          <div className="bg-gray-200 rounded h-[24rem] w-full" />
        </div>
      </div>
    </section>
  );
}

// ─── Tooltip ───────────────────────────────────────────────────────────────
function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block ml-[0.6rem] align-middle">
      <button
        type="button"
        aria-label="More information"
        className="w-[1.6rem] h-[1.6rem] rounded-full border border-gray-400 text-gray-400 text-[1rem] leading-none flex items-center justify-center hover:border-gray-600 hover:text-gray-600 focus:outline-hidden"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-[0.8rem] w-[22rem] bg-gray-800 text-white text-[1.2rem] leading-[1.8rem] rounded px-[1rem] py-[0.8rem] shadow-lg pointer-events-none"
        >
          {text}
        </span>
      )}
    </span>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function PricingGuideSection({ master, sellUrl, onVisibilityChange }: PricingGuideSectionProps) {
  const [data, setData] = useState<PricingApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('6mo');
  const [selectedCondition, setSelectedCondition] = useState<ConditionTab>('excellent_like_new');

  const marketValuations = (master as any).marketValuations as MarketValuations | undefined;

  const fetchData = useCallback(
    async () => {
      try {
        const res = await fetch(
          `/api/market/pricing?gearmaster_id=${master.id}`
        );
        if (!res.ok) throw new Error('API error');
        const json: PricingApiResponse = await res.json();
        setData(json);
        setFetchError(false);
      } catch {
        setFetchError(true);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [master.id]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Visibility gate (category-aware, recency-aware) ───────────────────
  const last12Months = data?.last12MonthsSalesCount ?? 0;
  const last6Months = data?.salesLast6moCount ?? 0;
  const overallMedian = data?.median ?? 0;
  const hasBySource = Boolean(
    marketValuations?.bySource && Object.keys(marketValuations.bySource).length > 0
  );

  // Classify cinema vs everything else; cinema bodies get the lower yearly floor.
  const { isCinema } = classifyCinema(master, overallMedian);
  const min12mo = isCinema ? MIN_12MO_CINEMA : MIN_12MO_DEFAULT;

  // Show the price section ONLY IF BOTH are true (recent activity AND yearly
  // volume). Either failing => render nothing for price. Replaces the old
  // last12Months >= 10 gate entirely.
  const hasEnoughSales =
    last6Months >= MIN_6MO_RECENT && last12Months >= min12mo;

  useEffect(() => {
    if (!loading) {
      onVisibilityChange?.(hasEnoughSales || hasBySource);
    }
  }, [loading, hasEnoughSales, hasBySource, onVisibilityChange]);

  if (loading) return <Skeleton />;

  // ── Insufficient-data path ────────────────────────────────────────────
  // Previously this returned null and the widget vanished silently. Instead we
  // render a subdued "not enough data" notice so the slot is acknowledged.
  //
  // CRITICAL VISIBILITY CONTRACT: the visibility effect above reports
  // `hasEnoughSales || hasBySource` — which is FALSE on this exact path (both
  // are false, that's how we got here). So the parent's `hasPricingGuide` stays
  // false and the "Pricing Guide" sticky tab does NOT appear. Both notices
  // below (fetch-error and genuine-no-data) are wrapped with the navy
  // "GearFocus Pricing Guide" banner (2026-06-19, per Bill) so the notice has
  // context. Neither flips visibility, and neither uses the #pricing-guide id,
  // so the sticky "Pricing Guide" tab stays hidden in both cases.
  //
  // FIX (web#1670): `hasEnoughSales` is derived from `data` fields defaulted
  // to 0 (`?? 0`), so a FAILED fetch and a GENUINE zero-sales response were
  // indistinguishable here — both fell into this branch and both rendered
  // "Not enough data yet," which is FALSE on a model that has plenty of sales
  // history when the fetch simply errored. `fetchError` (set by the catch
  // block in fetchData) is the only signal that tells the two cases apart, so
  // it gates which copy renders.
  if (!hasEnoughSales && !hasBySource) {
    if (fetchError) {
      return (
        <section aria-label="Pricing Guide">
          <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">

            {/* Header banner — identical to the real widget so the notice has context */}
            <div className="bg-[#242E5F] rounded-t-[0.8rem] px-[2rem] py-[1.2rem]">
              <h2 className="text-white text-[1.8rem] font-bold tracking-tight">
                GearFocus Pricing Guide
              </h2>
            </div>

            {/* Notice body in the bordered box below the banner — deliberately
                does NOT claim anything about the model's sales history, since
                we don't actually know it: the request failed, we didn't get
                an answer. */}
            <div className="border border-t-0 border-gray-200 rounded-b-[0.8rem] px-[2rem] py-[2rem]">
              <div role="note" className="flex flex-col sm:flex-row sm:items-start gap-[1.2rem]">
                {/* Info icon (decorative) */}
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-[2rem] h-[2rem] shrink-0 text-gray-400 mt-[0.2rem]"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>

                <div className="flex flex-col gap-[0.4rem]">
                  <h3 className="text-[1.6rem] font-semibold text-gray-700">
                    Pricing temporarily unavailable.
                  </h3>
                  <p className="text-[1.4rem] text-gray-500 mt-1 leading-[2.1rem]">
                    We couldn&apos;t load market pricing for the {master.title} right now — not
                    a reflection of how much sales data exists. Please check back shortly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section aria-label="Pricing Guide">
        <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">

          {/* Header banner — identical to the real widget so the notice has context */}
          <div className="bg-[#242E5F] rounded-t-[0.8rem] px-[2rem] py-[1.2rem]">
            <h2 className="text-white text-[1.8rem] font-bold tracking-tight">
              GearFocus Pricing Guide
            </h2>
          </div>

          {/* Notice body in the bordered box below the banner */}
          <div className="border border-t-0 border-gray-200 rounded-b-[0.8rem] px-[2rem] py-[2rem]">
            <div role="note" className="flex flex-col sm:flex-row sm:items-start gap-[1.2rem]">
              {/* Info icon (decorative) */}
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[2rem] h-[2rem] shrink-0 text-gray-400 mt-[0.2rem]"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>

              <div className="flex flex-col gap-[0.4rem]">
                <h3 className="text-[1.6rem] font-semibold text-gray-700">
                  Not enough data yet.
                </h3>
                <p className="text-[1.4rem] text-gray-500 mt-1 leading-[2.1rem]">
                  We need more sales of the {master.title} on record before we can publish a
                  reliable market value. When the data is there, it will show up here automatically.
                </p>
                <Link
                  href={sellUrl}
                  className="mt-[0.4rem] inline-flex w-fit items-center gap-[0.4rem] text-[1.4rem] font-semibold text-brand-secondary hover:text-brand-primary rounded-[0.4rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2"
                >
                  Have one to sell? List it →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── Section-level visibility flags ────────────────────────────────────
  // The sales-data section renders whenever the model clears BOTH gate halves.
  // (Decoupled from the old hard-coded >= 10 so the new floors are reachable.)
  const showSalesData = hasEnoughSales && data !== null && !fetchError;

  // ── Derived values ─────────────────────────────────────────────────────
  const { conditionRanges, priceHistory } = data ?? {
    conditionRanges: { excellent_like_new: null, good: null, fair: null },
    priceHistory: [],
  };

  // Trend badge: globally suppressed via SUPPRESS_TREND_BADGE while the matcher
  // is draining. When re-enabled, only shows when BOTH 90-day windows meet the
  // minimum sample count (one outlier in a thin window can flip the direction).
  const trendCounts = data?.trend90DayCounts ?? { last90: 0, prior90: 0 };
  const trendBadgeEligible =
    !SUPPRESS_TREND_BADGE &&
    trendCounts.last90 >= TREND_MIN_PER_WINDOW &&
    trendCounts.prior90 >= TREND_MIN_PER_WINDOW;
  const trendDirection = data?.trend90Day?.direction ?? 'flat';

  // Latest-sale freshness line: only show when the sales gate passes AND the
  // newest approved sale is recent (within DATE_FRESH_DAYS). The hasEnoughSales
  // guard keeps the date from showing for a product whose pricing we suppress
  // (e.g. when the widget renders only for retail bySource data).
  const latestSaleDate = data?.latestSaleDate ?? null;
  const showLatestSale =
    hasEnoughSales && latestSaleDate !== null && isWithinDays(latestSaleDate, DATE_FRESH_DAYS);

  // Used retail: global min/max across all bySource entries
  let usedRetailMin = Infinity;
  let usedRetailMax = -Infinity;
  if (hasBySource && marketValuations?.bySource) {
    for (const src of Object.values(marketValuations.bySource)) {
      if (src.min < usedRetailMin) usedRetailMin = src.min;
      if (src.max > usedRetailMax) usedRetailMax = src.max;
    }
  }
  const hasValidUsedRetail =
    hasBySource && isFinite(usedRetailMin) && isFinite(usedRetailMax);

  // Chart data (follows time window toggle)
  const filteredHistory = filterByWindow(priceHistory, timeWindow);
  const chartData = {
    labels: filteredHistory.map((h) => formatMonthLabel(h.month)),
    datasets: [
      {
        data: filteredHistory.map((h) => h.avgPrice),
        borderColor: '#C75008',
        backgroundColor: 'rgba(255, 105, 0, 0.15)',
        pointBackgroundColor: '#C75008',
        pointBorderColor: '#C75008',
        fill: true,
        tension: 0.35,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatPrice(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 }, color: '#6B7280' },
      },
      y: {
        grid: { color: '#F3F4F6' },
        ticks: {
          font: { size: 12 },
          color: '#6B7280',
          callback: (v: any) => formatPrice(v),
        },
      },
    },
  };

  // 30-day price delta: compare current condition range midpoint vs prior
  const { prior30DayRanges } = data ?? { prior30DayRanges: { excellent_like_new: null, good: null, fair: null } };

  // Condition tab config
  const conditionTabs: Array<{ key: ConditionTab; label: string }> = [
    { key: 'excellent_like_new', label: 'Like New / Excellent' },
    { key: 'good', label: 'Good' },
    { key: 'fair', label: 'Fair' },
    { key: 'poor', label: 'Poor / Not Functional' },
  ];

  // Get the condition range for the selected tab (null for 'poor').
  // GATE GUARD (FIX 1): force this to null whenever the sales gate fails, even
  // if the widget is still rendering for retail bySource data. selectedRange is
  // the single source for EVERY sold-comp price path — the Section 1 market
  // value / trade-in, the 30-day delta (which early-returns when selectedRange
  // is null), and the footer CTA "It's Worth $X+". Nulling it here suppresses
  // all of them at once so no sold-comp price can leak when the gate fails.
  const selectedRange =
    hasEnoughSales && selectedCondition !== 'poor'
      ? conditionRanges[selectedCondition as 'excellent_like_new' | 'good' | 'fair']
      : null;

  return (
    <section id="pricing-guide" aria-label="Pricing Guide">
      <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bg-[#242E5F] rounded-t-[0.8rem] px-[2rem] py-[1.2rem]">
          <h2 className="text-white text-[1.8rem] font-bold tracking-tight">
            GearFocus Pricing Guide
          </h2>
        </div>

        <div className="border border-t-0 border-gray-200 rounded-b-[0.8rem] divide-y divide-gray-200">

          {/* ── Section 1: Condition-based Price Ranges ─────────────── */}
          {showSalesData && (
            <div className="px-[2rem] py-[1.4rem]">
              {/* Condition tabs */}
              <div className="flex flex-wrap items-center gap-[0.8rem] mb-[1.2rem]">
                {conditionTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setSelectedCondition(tab.key);
                      trackMasterEvent('master_condition_select', master, {
                        section: 'pricing-guide',
                        condition: tab.key,
                      });
                    }}
                    className={`px-[1.4rem] py-[0.6rem] rounded-full text-[1.3rem] font-medium transition-colors ${
                      selectedCondition === tab.key
                        ? 'bg-[#242E5F] text-white'
                        : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Condition card content */}
              {selectedCondition === 'poor' ? (
                <div className="bg-gray-50 border border-gray-200 rounded-[0.8rem] px-[2rem] py-[1.6rem]">
                  <p className="text-[1.4rem] text-gray-600 leading-relaxed">
                    Due to the wide variability of items in poor or non-functional condition, we are unable to provide an accurate valuation estimate.
                  </p>
                </div>
              ) : selectedRange ? (
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-[1.6rem]">
                  {/* Left: market value + trade-in */}
                  <div className="flex flex-col gap-[1.2rem]">
                    <div>
                      {/* Market Value header row: label + optional trend badge */}
                      <div className="flex items-center gap-[1rem] mb-[0.4rem]">
                        <span className="text-[1.4rem] font-semibold text-gray-700">Market Value</span>

                        {/* Trend badge — currently suppressed globally via
                            SUPPRESS_TREND_BADGE while the matcher drains. When
                            re-enabled it shows only when both 90-day windows have
                            >= TREND_MIN_PER_WINDOW samples (one outlier in a thin
                            window can otherwise flip the direction). */}
                        {trendBadgeEligible && trendDirection !== 'flat' && (
                          <span
                            className={`inline-flex items-center gap-[0.3rem] px-[0.8rem] py-[0.2rem] rounded-full text-[1.1rem] font-semibold ${
                              trendDirection === 'appreciating'
                                ? 'bg-green-100 text-green-700'
                                : trendDirection === 'slight_depreciation'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {trendDirection === 'appreciating' ? '↑' : '↓'}
                            {trendDirection === 'appreciating'
                              ? 'Appreciating'
                              : trendDirection === 'slight_depreciation'
                              ? 'Softening'
                              : 'Depreciating'}
                          </span>
                        )}
                      </div>

                      {/* Price range */}
                      <div className="flex items-baseline gap-[1rem]">
                        <span className="text-[2.8rem] font-bold text-gray-900">
                          {formatPrice(selectedRange.low)}&nbsp;&mdash;&nbsp;{formatPrice(selectedRange.high)}
                        </span>
                      </div>
                    </div>

                    {/* Trade-in enclosed box */}
                    <div className="bg-gray-700 rounded-[0.6rem] px-[1.4rem] py-[1rem] inline-block text-center">
                      <div className="flex items-center justify-center gap-[0.6rem]">
                        <span className="text-[1.3rem] font-semibold text-gray-200">Estimated Trade-In</span>
                        <InfoTooltip text="Trade-in estimates are approximate. Actual dealer quotes vary by condition, inventory, and demand." />
                      </div>
                      <span className="text-[1.8rem] font-bold text-white block mt-[0.2rem]">
                        {formatPrice(selectedRange.tradeIn.low)}&nbsp;&mdash;&nbsp;{formatPrice(selectedRange.tradeIn.high)}
                      </span>
                    </div>

                  </div>

                  {/* Right: 30-Day Price Delta */}
                  {(() => {
                    const condKey = selectedCondition as 'excellent_like_new' | 'good' | 'fair';
                    const priorRange = prior30DayRanges[condKey];
                    if (!selectedRange || !priorRange) return null;
                    const currentMid = (selectedRange.low + selectedRange.high) / 2;
                    const priorMid = (priorRange.low + priorRange.high) / 2;
                    const delta = Math.round(currentMid - priorMid);
                    const absDelta = Math.abs(delta);
                    const isDown = delta < -5;
                    const isUp = delta > 5;
                    const arrow = isDown ? '↓' : isUp ? '↑' : '—';
                    const color = isDown ? '#D85A30' : isUp ? '#0F6E56' : '#6B7280';
                    return (
                      <div className="flex flex-col gap-[0.4rem] sm:items-end pr-[0.8rem]">
                        <span className="text-[1.6rem] font-semibold text-gray-500">Past 30 days</span>
                        <span style={{ color, fontSize: '2.5rem', fontWeight: 500 }}>
                          {arrow} {formatPrice(absDelta)}
                        </span>
                        <span className="text-[1.5rem] text-gray-400">
                          from {formatPrice(priorRange.low)} — {formatPrice(priorRange.high)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-[0.8rem] px-[2rem] py-[1.6rem]">
                  <p className="text-[1.4rem] text-gray-500">
                    Not enough sales data to estimate a price range for this condition.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Section 2: Used Retail Price ───────────────────────── */}
          {hasValidUsedRetail && (
            <div className="px-[2rem] py-[1.6rem]">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[0.8rem]">
                <span className="text-[1.6rem] font-semibold text-gray-800">Used Retail Price</span>
                <div className="flex flex-col items-start sm:items-end gap-[0.2rem]">
                  <span className="text-[2rem] font-bold text-gray-900">
                    {formatPrice(usedRetailMin)}&nbsp;–&nbsp;{formatPrice(usedRetailMax)}
                  </span>
                  <span className="text-[1.2rem] text-gray-400">
                    Based on retail asking prices from professional dealers
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Section 3: Price History Chart (full-width) */}
          {showSalesData && filteredHistory.length > 0 && (
            <div className="px-[1.2rem] py-[1.2rem]">
              <div className="bg-gray-50 rounded-[0.8rem] px-[1.6rem] py-[1.6rem]">
                {/* Header row: title left, toggle pills right */}
                <div className="flex items-center justify-between mb-[1.2rem]">
                  <h3 className="text-[1.6rem] font-semibold text-gray-800">Price History</h3>
                  <div className="flex items-center gap-[0.6rem]">
                    {(Object.keys(TIME_WINDOW_LABELS) as TimeWindow[]).map((w) => (
                      <button
                        key={w}
                        type="button"
                        onClick={() => {
                          setTimeWindow(w);
                          trackMasterEvent('master_price_history_range', master, {
                            section: 'pricing-guide',
                            time_window: w,
                          });
                        }}
                        className={`px-[1.2rem] py-[0.4rem] rounded-full text-[1.2rem] font-medium transition-colors ${
                          timeWindow === w
                            ? 'bg-[#242E5F] text-white'
                            : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {TIME_WINDOW_LABELS[w]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Chart — reduced height */}
                <div style={{ height: '22rem' }}>
                  <Line data={chartData} options={chartOptions as any} />
                </div>

                <p className="text-[1.2rem] text-gray-400 mt-[0.8rem]">
                  *Prices reflect monthly average for sales of items in used condition
                </p>
              </div>
            </div>
          )}

          {/* ── Section 5: CTA (always shown when widget is visible) ── */}
          <div className="px-[2rem] py-[1.4rem] flex flex-col items-center gap-[1rem]">
            {/* Freshness line — shown ONLY when the newest approved sale is
                within DATE_FRESH_DAYS (14). Older than that, hide it entirely
                rather than advertise a stale "latest sale". */}
            {showLatestSale && latestSaleDate && (
              <p className="text-[1.2rem] text-gray-500 text-center">
                Latest sale: {formatLatestSaleDate(latestSaleDate)}
              </p>
            )}

            {/* Disclaimer above CTA */}
            <p className="text-[1.2rem] text-gray-400 text-center max-w-[40rem]">
              Prices shown are estimates based on observed market data and are meant to help you price confidently — not as a guarantee. Actual prices vary by condition, demand, and timing. Excludes shipping and taxes.
            </p>

            {/* CTA — dynamic price from selected condition */}
            <Link
              href={sellUrl}
              className="inline-block bg-primary text-white font-bold text-[1.6rem] rounded-[0.8rem] px-[2.4rem] py-[1.2rem] hover:bg-primary-hover transition-colors"
            >
              {selectedRange
                ? `List Your ${master.model} — It's Worth ${formatPrice(selectedRange.low)}+`
                : `List Your ${master.model}`
              }
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}
